/**
 * 🧑‍🏫 반 — 열기 · 들어가기 · 손들기 · 반 한 장.
 *
 * 🔴 `EDGE_BASE` 를 반드시 앞에 붙인다(dev 에서 웹과 엣지가 다른 출처라 상대경로는 조용히 실패).
 * 🔴 실패해도 던지지 않는다. 학교 와이파이가 끊겼다고 수업 화면이 하얗게 되면 안 된다 —
 *    화면이 「지금 못 불러왔어요」를 말하고 다음 4초에 다시 시도한다(불변식 3의 폴백 경계).
 */
import type { MeetVerdict } from "@greg-chick/shared";
import { safeMeetUrl, type ClassMember, type SavedHero } from "@greg-chick/shared";
import { EDGE_BASE } from "./apiBase";
import { pushOutcome, type PushResult } from "./storyApi";

async function post(path: string, body: unknown): Promise<unknown | null> {
  try {
    const res = await fetch(`${EDGE_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export interface OpenedClass {
  classCode: string;
  /** 처음 연 사람만 받는다. 이미 있는 반이면 없음. */
  teacherKey?: string;
  taken?: boolean;
  /** 적어 둔 열쇠를 같이 보냈을 때, 그게 맞는지. 안 보냈으면 없음. */
  teacherOk?: boolean;
}

/**
 * 반을 연다. 처음이면 선생님 열쇠가 온다.
 *
 * 이미 있는 반에 **적어 둔 열쇠**를 함께 보내면 맞는지 확인해 준다
 * (맞다/아니다만 온다 — 열쇠 자체는 안 돌려준다).
 */
export async function openClass(
  classCode: string,
  teacherKey?: string,
): Promise<OpenedClass | null> {
  const r = (await post("/api/class/open", { classCode, teacherKey })) as OpenedClass | null;
  return r && typeof r.classCode === "string" ? r : null;
}

/**
 * 아이가 이 반에 들어왔다고 알린다. 실패해도 수업은 굴러간다(글이 올라가면 그때 잡힌다).
 *
 * 🔴 캐릭터 **모양**도 함께 보낸다. 전시장·선생님 화면이 이걸로 아이를 그리는데,
 * 아이 캐릭터는 이 기기에만 있어도 되므로(M1) 서버에서 조회할 수가 없다.
 */
export async function joinClass(classCode: string, hero: SavedHero): Promise<PushResult> {
  // 코드가 없으면 보낼 곳이 없다 — 다시 시도해도 소용없다
  if (!hero.code) return "gone";
  try {
    const res = await fetch(`${EDGE_BASE}/api/class/join`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        classCode,
        heroCode: hero.code,
        nickname: hero.nickname,
        hero: { base: hero.base, parts: hero.parts, palette: hero.palette },
      }),
    });
    // 🔴 「다시 해도 소용없나」를 그림책과 **같은 규칙**으로 판단한다(pushOutcome).
    //    두 곳에서 다르게 정하면 하나는 영영 두드리고 하나는 너무 일찍 포기한다.
    return pushOutcome(res.status);
  } catch {
    return "retry"; // 와이파이가 끊긴 것 — 인터넷이 오면 다시(경계 폴백)
  }
}

/** 손들기(아이) · 잘했어요(선생님 열쇠 필요). `on:false` 면 내린다. */
export async function sendSignal(args: {
  classCode: string;
  heroCode: string;
  kind: "hand" | "praise";
  on?: boolean;
  teacherKey?: string;
}): Promise<boolean> {
  return (await post("/api/class/signal", args)) !== null;
}

/**
 * 🙈 **전시장에서 빼기 · 다시 걸기**(선생님 열쇠 필요).
 *
 * `heroCode` 를 안 주면 **지금 빠져 있는 아이 목록만** 받는다 — 선생님 화면이 열릴 때
 * 「누가 내려져 있나」를 알아야 다시 걸 수 있다.
 *
 * 🔴 못 받았을 때 **빈 배열이 아니라 `null`** 을 준다. 빈 배열로 뭉뚱그리면 화면은
 * 「아무도 안 빠졌다」로 그리고, 선생님은 내려 둔 글이 **다시 걸린 줄 안다.**
 */
export async function setStoryShown(args: {
  classCode: string;
  teacherKey: string;
  heroCode?: string;
  /** 참이면 **뺀다**. 안 적으면 다시 거는 쪽 — 빼는 건 분명히 말해야 한다. */
  hide?: boolean;
}): Promise<string[] | null> {
  const body = await post("/api/class/hide", {
    classCode: args.classCode,
    teacherKey: args.teacherKey,
    ...(args.heroCode ? { heroCode: args.heroCode, on: args.hide === true } : {}),
  });
  const hidden = (body as { hidden?: unknown } | null)?.hidden;
  return Array.isArray(hidden) ? hidden.filter((v): v is string => typeof v === "string") : null;
}

/** 반 한 장. 못 받으면 null — 화면이 앞서 받은 것을 그대로 두게 하려고 빈 배열이 아니다. */
/**
 * 반 명단·진도 — **정리된 반이면 `"gone"`**.
 *
 * 🔴 빈 명단으로 뭉뚱그리면 선생님은 자기 반이 사라진 줄 알고 새 반을 만든다.
 * 그러면 아이들이 코드를 다시 넣어야 하고 지금까지 쓴 글은 이전 반에 남는다.
 */
export async function loadClassBoardEx(
  classCode: string,
): Promise<{ rows: ClassMember[] | null; gone: boolean; cut: boolean; meetUrl: string | null }> {
  const none = { rows: null, gone: false, cut: false, meetUrl: null };
  try {
    const res = await fetch(`${EDGE_BASE}/api/class/${encodeURIComponent(classCode)}`, {
      cache: "no-store",
    });
    if (res.status === 410) return { rows: [], gone: true, cut: false, meetUrl: null };
    if (!res.ok) return none;
    const body = (await res.json()) as { members?: unknown; cut?: unknown; meetUrl?: unknown };
    if (!Array.isArray(body.members)) return none;
    return {
      rows: body.members.filter(
        (m): m is ClassMember => !!m && typeof (m as ClassMember).heroCode === "string",
      ),
      gone: false,
      // 🔴 서버가 명단을 잘랐다 — 조용히 두면 선생님이 안 온 아이를 찾아다닌다
      cut: body.cut === true,
      /**
       * 🎥 선생님이 넣어 둔 우리 반 화상 링크. 서버가 걸러서 주지만 **여기서 또 거른다** —
       * 이 값은 곧 아이가 눌러서 새 창을 여는 주소가 된다(불변식 7).
       */
      meetUrl: safeMeetUrl(body.meetUrl),
    };
  } catch {
    return none; // 못 받았다 — 「없다」와 다르다
  }
}

/**
 * 🎥 **우리 반 화상 링크를 바꾼다** — 선생님만.
 *
 * 빈 문자열을 보내면 **지우기**다(예전 방식으로 돌아간다) — 잘못이 아니다.
 * 무엇이 잘못됐는지 갈라서 돌려준다. 화면이 「지금 못 했어요」와 「이 반의 열쇠가
 * 아니에요」와 「주소가 이상해요」를 다르게 말해야 한다.
 */
export type MeetSaveResult = "ok" | "not_teacher" | "bad_url" | "offline";

export async function saveClassMeetUrl(
  classCode: string,
  teacherKey: string,
  meetUrl: string,
): Promise<MeetSaveResult> {
  try {
    const res = await fetch(`${EDGE_BASE}/api/class/meet`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ classCode, teacherKey, meetUrl }),
    });
    if (res.ok) return "ok";
    if (res.status === 403) return "not_teacher";
    if (res.status === 400) return "bad_url";
    return "offline";
  } catch {
    return "offline";
  }
}

/**
 * 🔎 붙여넣은 화상 주소를 **서버가 한 번 열어 보게** 한다(저장하지 않는다).
 *
 * 브라우저는 교차출처 응답 헤더를 못 읽어서 「없는 방인가」·「끼워 넣기가 되는가」를
 * 알 수 없다. 판정은 shared `meetVerdict` 한 곳 — 관리자 화면과 **같은 말**을 한다.
 *
 * 🔴 못 물어봤으면 `null` — 화면은 **아무 말도 안 해야** 한다.
 */
export async function checkClassMeetUrl(
  classCode: string,
  teacherKey: string,
  meetUrl: string,
): Promise<MeetVerdict | null> {
  try {
    const res = await fetch(`${EDGE_BASE}/api/class/meet/check`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ classCode, teacherKey, meetUrl }),
    });
    if (!res.ok) return null;
    return (await res.json()) as MeetVerdict;
  } catch {
    return null;
  }
}

export async function loadClassBoard(classCode: string): Promise<ClassMember[] | null> {
  try {
    const res = await fetch(`${EDGE_BASE}/api/class/${encodeURIComponent(classCode)}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { members?: unknown };
    if (!Array.isArray(body.members)) return null;
    return body.members.filter(
      (m): m is ClassMember => !!m && typeof (m as ClassMember).heroCode === "string",
    );
  } catch {
    return null;
  }
}
